import { Grid, Select } from "antd";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { useAuth } from "../app/auth.tsx";
import { classKeys, defaultYear, useAcademicYears } from "../app/classes.ts";

/** Enough for every teacher the school has ever had (the list endpoint's page limit). */
const TEACHER_PAGE_SIZE = 200;

export interface TeacherMessageLogFilterValues {
  teacherId: string | undefined;
  classId: string | undefined;
}

/**
 * Teacher and class filters of the teacher message log (bd-3qg.22). Teachers include inactive ones, because the log keeps
 * their messages; classes are those of the active (otherwise the latest) academic year. Each picker shows only when the
 * user may read its source list (people.read, academic.read).
 */
export function TeacherMessageLogFilters({ value, onChange }: { value: TeacherMessageLogFilterValues; onChange: (value: TeacherMessageLogFilterValues) => void }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const screens = Grid.useBreakpoint();
  const canFilterTeachers = hasPermission("people.read");
  const canFilterClasses = hasPermission("academic.read");

  const teachers = useQuery({
    queryKey: ["teachers", "all-for-filter"],
    enabled: canFilterTeachers,
    queryFn: async () => {
      const { data, error } = await api.GET("/teachers", { params: { query: { Page: 1, PageSize: TEACHER_PAGE_SIZE } } });
      if (error || !data) throw new Error(t("app.error"));
      return data.items;
    },
  });
  const years = useAcademicYears();
  const year = defaultYear(years.data);
  const classes = useQuery({
    queryKey: classKeys.list(year?.id),
    enabled: canFilterClasses && year !== undefined,
    queryFn: async () => {
      const { data, error } = await api.GET("/academic/classes", { params: { query: year ? { AcademicYearId: year.id } : {} } });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
  });

  const teacherOptions = (teachers.data ?? []).map((teacher) => {
    const name = `${teacher.lastName} ${teacher.firstName}`;
    return { value: teacher.id, label: teacher.isActive ? name : t("teacherMessages.filters.inactiveTeacher", { name }) };
  });

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
      {canFilterTeachers && (
        <Select
          allowClear
          showSearch={{ optionFilterProp: "label" }}
          value={value.teacherId}
          placeholder={t("teacherMessages.filters.teacher")}
          aria-label={t("teacherMessages.filters.teacher")}
          loading={teachers.isPending}
          options={teacherOptions}
          style={{ width: screens.md ? 260 : "100%" }}
          onChange={(teacherId?: string) => onChange({ ...value, teacherId })}
        />
      )}
      {canFilterClasses && (
        <Select
          allowClear
          showSearch={{ optionFilterProp: "label" }}
          value={value.classId}
          placeholder={t("teacherMessages.filters.class")}
          aria-label={t("teacherMessages.filters.class")}
          loading={classes.isPending && classes.fetchStatus !== "idle"}
          options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
          style={{ width: screens.md ? 160 : "100%" }}
          onChange={(classId?: string) => onChange({ ...value, classId })}
        />
      )}
    </div>
  );
}
